import i18n from './index';
import annualCopy from './copy/annual.json';

import { normalizePreference, resolveLanguage, storedPreference } from './preference';
export { LANGUAGES, normalizeLanguage, normalizePreference, deviceLanguage, resolveLanguage } from './preference';

let preference = storedPreference();
let applicationVersion = 0;
const listeners = new Set();
export const currentPreference = () => preference;
export const subscribePreference = listener => { listeners.add(listener); return () => listeners.delete(listener); };
const loaders = {
  en: () => import('./copy/en.json'),
  de: () => import('./copy/de.json'),
  fr: () => import('./copy/fr.json'),
  es: () => import('./copy/es.json'),
};
const pending = new Map();
const savedInSession = new Map();
export async function prepareLanguage(value) {
  const language = resolveLanguage(value);
  if (language !== 'it' && !i18n.hasResourceBundle(language, 'copy')) {
    if (!pending.has(language)) pending.set(language, loaders[language]().then(module => {
      i18n.addResourceBundle(language, 'copy', { ...(module.default || module), ...annualCopy[language] }, true, true);
    }).finally(() => pending.delete(language)));
    await pending.get(language);
  }
  return language;
}
export async function applyLanguage(value, isCurrent = () => true) {
  const version = ++applicationVersion;
  const next = normalizePreference(value);
  const language = await prepareLanguage(next);
  if (!isCurrent() || version !== applicationVersion) return null;
  await i18n.changeLanguage(language);
  preference = next;
  try {
    localStorage.setItem('lang', language);
    localStorage.setItem('tps:language-preference', next);
  } catch { /* Private browsing can disable storage. */ }
  if (typeof document !== 'undefined') document.documentElement.lang = language;
  listeners.forEach(listener => listener());
  return language;
}
export function sessionLanguage(user) {
  // The API distinguishes an explicit personal choice from the legacy Studio
  // fallback. Never turn an inherited Studio setting into a manual preference.
  return savedInSession.get(String(user?.id || '')) || normalizePreference(user?.language_preference ?? user?.language ?? 'auto');
}
export async function saveLanguage(value, userId, save, isCurrent = () => true) {
  const next = normalizePreference(value);
  await prepareLanguage(next);
  if (!isCurrent()) return null;
  if (save) await save(next);
  if (!isCurrent()) return null;
  if (userId) savedInSession.set(String(userId), next);
  return applyLanguage(next, isCurrent);
}
export function watchDeviceLanguage(onError = () => {}) {
  const refresh = () => {
    if (document.visibilityState === 'hidden' || preference !== 'auto' || resolveLanguage('auto') === i18n.language) return;
    applyLanguage('auto', () => preference === 'auto').catch(onError);
  };
  window.addEventListener('languagechange', refresh);
  window.addEventListener('pageshow', refresh);
  document.addEventListener('visibilitychange', refresh);
  return () => {
    window.removeEventListener('languagechange', refresh);
    window.removeEventListener('pageshow', refresh);
    document.removeEventListener('visibilitychange', refresh);
  };
}
